import BigNumber from "bignumber.js";
import { useRouter } from "next/navigation";
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useChains, useConfig } from "wagmi";
import { waitForTransactionReceipt } from "wagmi/actions";

import { getTokenSymbol } from "@/app/util";
import { INVALID_AMOUNT_ERROR } from "@/components/trade/constants";
import { useOpenPosition } from "@/components/trade/hooks/open-position";
import { useWithdrawAll } from "@/components/trade/hooks/withdraw-all";
import { PositionDirection } from "@/components/trade/state";
import { PARAMS_DECIMALS } from "@/constants/common";
import { TEN, ZERO } from "@/constants/math";
import { useRx } from "@/contexts/rx";
import { PositionType } from "@/contracts/transform/position";
import { useDisplayError } from "@/hooks/error";
import { usePoolParams, usePositionsInfo } from "@/hooks/position";
import { isNumStr, noop } from "@/util/core";
import { withExactMessage } from "@/util/error";
import { formatCoin } from "@/util/format-coin";
import { getLiquidationPrice } from "@/util/math";
import { trackTradeSuccess } from "@/util/tracking";

import TradeContext from "./context";
import { ButtonState } from "./types";

/** @deprecated needs renaming */
export const useSetInitialAmount = () => {
  const preventSetAmount = useRef(false);

  const {
    base,
    basePrice,
    quote,
    realBaseAmount,
    realQuoteAmount,
    type,
    dispatch,
    flags,
    ...state
  } = useContext(TradeContext);

  // set initial value
  // v1   - direction === "long" ? baseAmount : quoteAmount
  //
  // v1.5 - lentAmount = baseAmount * price + quoteAmount
  //        direction === "long" ? lentAmount / price : lentAmount

  const [realAmount, decimals] = useMemo(() => {
    if (!flags?.flip) {
      return state.direction === "long"
        ? [realBaseAmount, base?.decimals]
        : [realQuoteAmount, quote?.decimals];
    }

    const lentAmount = basePrice
      ? realBaseAmount?.times(basePrice).plus(realQuoteAmount ?? 0)
      : undefined;

    return state.token === "base"
      ? [basePrice ? lentAmount?.div(basePrice) : undefined, base?.decimals]
      : [lentAmount, quote?.decimals];
  }, [
    basePrice,
    flags?.flip,
    realBaseAmount,
    realQuoteAmount,
    state.direction,
    state.token,
    base?.decimals,
    quote?.decimals,
  ]);

  useEffect(() => {
    preventSetAmount.current =
      !type || [PositionType.Long, PositionType.Short].includes(type)
        ? true
        : false;
  }, [state.direction, type]);

  useEffect(() => {
    if (!realAmount || preventSetAmount.current || !decimals || !type) {
      return;
    }

    dispatch?.({
      type: "setDepositAmount",
      depositAmount: realAmount.gt(0) ? realAmount.toString() : "",
      basePrice,
    });

    preventSetAmount.current = true;
  }, [basePrice, decimals, dispatch, realAmount, type]);

  return {
    realAmount,
  };
};

/** @deprecated todo cleanup */
const useTrade = (
  {
    needApprove,
    realAmount,
    refetchRef,
    unwrap,
  }: {
    realAmount?: BigNumber;
    needApprove?: boolean;
    refetchRef: MutableRefObject<(() => Promise<void>) | undefined>;
    /** @deprecated fixme decide if it's ok */
    unwrap?: `0x${string}`;
  },
  chainName: string,
) => {
  const {
    address: poolAddress,
    base,
    baseBalance,
    basePrice,
    quote,
    quoteBalance,
    realBaseAmount,
    realQuoteAmount,
    type,
    dispatch,
    refetch: positionRefetch,
    flags,
    ...state
  } = useContext(TradeContext);

  const params = usePoolParams(poolAddress);
  const cancelRef = useRef<() => void>();
  const [_pending, setPending] = useState(false);
  const router = useRouter();
  const { error$ } = useRx();
  const position = usePositionsInfo(poolAddress, { disableLiqPrice: true });
  const [isWithdrawPanelVisible, setIsWithdrawPanelVisible] = useState(false);
  const [withdrawPanelClass, setWithdrawPanelClass] = useState("");

  const [minBaseAmount, minQuoteAmount] = useMemo(() => {
    if (!params?.positionMinAmount || !basePrice || !base?.decimals) {
      return [];
    }

    const minBaseAmount = TEN.pow(-base.decimals).times(
      params.positionMinAmount.toString(),
    );

    const minQuoteAmount = minBaseAmount.times(basePrice);
    return [minBaseAmount, minQuoteAmount];
  }, [params?.positionMinAmount, basePrice, base?.decimals]);

  const balance = useMemo(
    () => (state.token === "base" ? baseBalance : quoteBalance),
    [baseBalance, quoteBalance, state.token],
  );

  const liquidationPrice = useMemo(
    () =>
      getLiquidationPrice({
        direction: state.direction,
        quoteAmount: state.positionSize?.quoteAmount,
        baseAmount: state.positionSize?.baseAmount,
      }),
    [state.direction, state.positionSize],
  );

  const depositToken = state.token === "base" ? base : quote;

  const swapFee = useMemo(() => {
    if (!params || !state.depositAmount || !state.leverage || !basePrice) {
      return;
    }

    const price = state.direction === "long" ? basePrice : 1;

    const quoteLeveraged = ZERO.plus(state.depositAmount)
      .times(state.leverage - 1)
      .times(price);

    const { swapFee } = params;
    return quoteLeveraged.times(swapFee ?? 0).div(TEN.pow(PARAMS_DECIMALS));
  }, [basePrice, params, state.depositAmount, state.direction, state.leverage]);

  const notEnoughFunds = useMemo(() => {
    if (!state.depositAmountBn) {
      return false;
    }

    const _balance = state.token
      ? { baseBalance, quoteBalance }[`${state.token}Balance`]
      : undefined;

    if (!_balance) {
      // prevent flicker
      return false;
    }

    if (realAmount?.gt(0) && state.depositAmountBn.minus(realAmount).lte(0)) {
      // if already lending, do not check balance below position value
      return false;
    }

    if (
      state.depositAmountBn
        .minus(realAmount ?? 0)
        .times(TEN.pow(_balance.decimals))
        .lte(_balance.value.toString())
    ) {
      return false;
    }

    return true;
  }, [
    baseBalance,
    quoteBalance,
    realAmount,
    state.depositAmountBn,
    state.token,
  ]);

  const [shouldWithdrawBase, shouldWithdrawQuote] = useMemo<
    [boolean, boolean]
  >(() => {
    if (type !== PositionType.Lend || !realBaseAmount || !realQuoteAmount) {
      return [false, false];
    }

    if (!flags?.flip) {
      if (state.direction === "long") {
        return [
          isNumStr(state.depositAmount)
            ? !realBaseAmount.lte(state.depositAmount)
            : false,
          realQuoteAmount.gt(0),
        ];
      } else if (state.direction === "short") {
        return [
          realBaseAmount.gt(0),
          isNumStr(state.depositAmount)
            ? !realQuoteAmount.lte(state.depositAmount)
            : false,
        ];
      }
    } else {
      if (!realAmount?.gt(0)) {
        return [false, false];
      }

      return [
        state.token !== "base"
          ? false
          : isNumStr(state.depositAmount)
          ? !realAmount.lte(state.depositAmount)
          : false,
        state.token !== "quote"
          ? false
          : isNumStr(state.depositAmount)
          ? !realAmount.lte(state.depositAmount)
          : false,
      ];
    }

    return [false, false];
  }, [
    flags?.flip,
    realAmount,
    realBaseAmount,
    realQuoteAmount,
    state.direction,
    state.depositAmount,
    state.token,
    type,
  ]);

  const shouldWithdraw = shouldWithdrawBase || shouldWithdrawQuote;

  const minPositionAmount =
    realBaseAmount?.gt(0) && realQuoteAmount?.gt(0)
      ? undefined
      : state.direction === "long" && type === PositionType.Lend
      ? realBaseAmount
      : state.direction === "short" && type === PositionType.Lend
      ? realQuoteAmount
      : undefined;

  const withdrawType = minPositionAmount?.gt(0)
    ? state.direction === "short"
      ? "quote"
      : "base"
    : state.direction === "short" && shouldWithdrawBase
    ? "base"
    : "quote";

  const withdraw = useWithdrawAll({
    enabled: shouldWithdraw,
    address: poolAddress,
    tokenType: withdrawType,
    token: withdrawType === "base" ? base : quote,
    unwrap,
  });

  const minPositionSize = state.leverage
    ? (state.token === "base" ? minBaseAmount : minQuoteAmount)?.div(
        state.leverage - 1,
      )
    : undefined;

  const lessThanMinPositionSize = state.depositAmountBn?.gt(0)
    ? state.depositAmountBn.lt(minPositionSize ?? 0)
    : false;

  const open = useOpenPosition({
    address: poolAddress,
    balance,
    basePrice,
    baseDecimals: base?.decimals,
    quoteDecimals: quote?.decimals,
    enabled:
      !_pending &&
      !lessThanMinPositionSize &&
      !notEnoughFunds &&
      !shouldWithdraw &&
      // !needApprove &&
      state.depositAmountBn?.gt(0),
    realAmount,
    state,
    token: depositToken,
    unwrap,
  });

  const buttonState = useMemo<ButtonState>(() => {
    if (notEnoughFunds) return "notEnoughFunds";
    if (lessThanMinPositionSize) return "lessThanMin";
    if (shouldWithdrawBase) return "shouldWithdrawBase";
    if (shouldWithdrawQuote) return "shouldWithdrawQuote";
    return "deposit";
  }, [
    notEnoughFunds,
    shouldWithdrawBase,
    shouldWithdrawQuote,
    lessThanMinPositionSize,
  ]);

  const positionDesc = useMemo(() => {
    if (!state.positionSize || !base || !quote) return [null, null];
    if (state.direction === "long") {
      return [
        <span key="first">
          With {formatCoin(state.depositAmount)}{" "}
          {getTokenSymbol(state.token === "base" ? base.symbol : quote.symbol)}
        </span>,
        <span key="second">
          +{" "}
          {formatCoin(
            state.positionSize.quoteAmount.div(
              state.token === "base" ? 1 : basePrice ?? 1,
            ),
          )}{" "}
          {getTokenSymbol(quote.symbol)}
        </span>,
      ];
    }

    return [
      <span key="first">
        With {formatCoin(state.depositAmount)}{" "}
        {getTokenSymbol(state.token === "base" ? base.symbol : quote.symbol)}
      </span>,
      <span key="second">
        + {formatCoin(state.positionSize.baseAmount)} {base.symbol}
      </span>,
    ];
  }, [
    base,
    basePrice,
    quote,
    state.depositAmount,
    state.direction,
    state.positionSize,
    state.token,
  ]);

  const showWithdrawPanel = useCallback(() => {
    setIsWithdrawPanelVisible(true);
    setTimeout(() => setWithdrawPanelClass("visible"), 0);
    return undefined;
  }, []);

  const hideWithdrawPanel = useCallback(() => {
    setWithdrawPanelClass("");
    setTimeout(() => setIsWithdrawPanelVisible(false), 350);
  }, []);

  const refetch = useCallback(() => {
    return Promise.all(
      [
        withdraw.params.data ? withdraw.params.refetch() : undefined,
        open.params.data ? open.params.refetch() : undefined,
      ].filter(Boolean),
    ).then(() => {
      return;
    });
  }, [withdraw.params, open.params]);

  // todo how to pass refetch more gracefully?
  useEffect(() => {
    refetchRef.current = refetch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  const { lastError, withMessage } = useDisplayError(refetch);

  const config = useConfig();
  const submitAction: (() => undefined) | (() => Promise<`0x${string}`>) =
    useMemo(() => {
      if (shouldWithdraw) {
        if (!isWithdrawPanelVisible) {
          return showWithdrawPanel;
        }
        if (withdraw.params.error) {
          return lastError;
        }
        const request = withdraw.params.data?.request;
        if (!request) {
          return noop;
        }
        return () => withdraw.tx.writeContractAsync(request);
      }

      if (!state.depositAmountBn || !state.depositAmountBn.isFinite()) {
        return withMessage.bind(null, INVALID_AMOUNT_ERROR);
      }

      if (open.params.error) {
        return lastError;
      }
      const request = open.params.data?.request;

      if (notEnoughFunds || !request) {
        return noop;
      }
      return () => open.tx.writeContractAsync(request);
    }, [
      isWithdrawPanelVisible,
      lastError,
      notEnoughFunds,
      open.params.data?.request,
      open.params.error,
      open.tx,
      shouldWithdraw,
      showWithdrawPanel,
      state.depositAmountBn,
      withMessage,
      withdraw.params.data?.request,
      withdraw.params.error,
      withdraw.tx,
    ]);

  const [pt, setPt] = useState<string>();
  const _title = shouldWithdraw ? withdraw.title : open.title;
  const reset = shouldWithdraw ? withdraw.tx.reset : open.tx.reset;
  const chains = useChains();
  const handleSubmit = useCallback(() => {
    // transaction send should be called as soon as possible to avoid wallet linking issues in IOS
    // every prerequisites should be checked before calling this function
    // see: https://docs.walletconnect.com/2.0/ios/guides/mobile-linking#ios-app-link-constraints
    const result: undefined | Promise<`0x${string}`> = submitAction();

    const cancelPromise = new Promise<void>((resolve) => {
      cancelRef.current = resolve;
    });

    const isFinal =
      !shouldWithdraw && open.params?.data?.request.functionName === "execute";

    if (result) {
      setPending(true);
      setPt(_title);

      Promise.race([result, cancelPromise])
        .then((res) => {
          if (res) {
            setIsWithdrawPanelVisible(false);
            return waitForTransactionReceipt(config, {
              confirmations: 2,
              hash: res,
            });
          } else {
            reset();
            throw withExactMessage(new Error("Canceled"));
          }
        })
        .then((receipt) => {
          positionRefetch?.();

          if (isFinal && state.positionSize && quote && base && receipt) {
            /*mutate(
              "positionDetails",
              internalChainId!,
              poolAddress!,
              account.address!,
            );*/

            const debtParams =
              state.direction === "long"
                ? `&initialAmount=${state.depositAmount}&debtAmount=${state.positionSize.quoteAmount}&debtToken=${quote.symbol}`
                : `&initialAmount=${state.depositAmount}&debtAmount=${state.positionSize.baseAmount}&debtToken=${base.symbol}&collateralToken=${quote.symbol}`;

            state.direction &&
              state.depositAmount &&
              trackTradeSuccess(state.direction, state.depositAmount);

            router.push(
              `/${chainName}/open-position-success?direction=${
                state.direction
              }&poolAddress=${poolAddress}&positionAmount=${state.positionSize?.total.toString()}&positionToken=${
                base.symbol
              }${debtParams}`,
            );
          } else {
            setPending(false);
            setPt(undefined);
          }
        })
        .catch((e) => {
          setPending(false);
          setPt(undefined);
          console.error(e);
          error$.next(e);
        });
    }
  }, [
    poolAddress,
    submitAction,
    shouldWithdraw,
    open.params?.data?.request.functionName,
    _title,
    reset,
    positionRefetch,
    state.positionSize,
    state.direction,
    state.depositAmount,
    quote,
    base,
    router,
    error$,
    chainName,
  ]);

  const pending =
    _pending ||
    open.tx.isPending ||
    open.params.isLoading ||
    withdraw.params.isLoading;

  const pendingTitle = _pending || open.tx.isPending ? pt : undefined;

  const onInputChange = useCallback(
    ({
      amount,
      leverage,
      direction,
    }: {
      amount?: string;
      leverage?: number;
      direction?: PositionDirection;
    }) => {
      if (!basePrice) return;

      if (typeof amount !== "undefined") {
        dispatch?.({
          type: "setDepositAmount",
          depositAmount: amount,
          basePrice,
        });
      } else if (leverage && Number.isFinite(leverage)) {
        dispatch?.({ type: "setLeverage", leverage, basePrice });
      } else if (direction) {
        dispatch?.({ type: "setDirection", direction });
      }
    },
    [basePrice, dispatch],
  );

  return {
    amount: open.amount,
    base,
    buttonState,
    cancelRef,
    depositAmount: state.depositAmount,
    fundingRate:
      state.direction === "long"
        ? position?.borrowCurrentQuoteApr
        : position?.borrowCurrentBaseApr,
    onInputChange,
    handleSubmit,
    hideWithdrawPanel,
    isWithdrawPanelVisible,
    leverage: Math.min(state.maxLeverage || 0, state.leverage || 0),
    liquidationPrice,
    minPositionAmount,
    notEnoughFunds,
    pending,
    pendingTitle,
    positionDesc,
    positionSize: state.positionSize,
    minPositionSize,
    lessThanMinPositionSize,
    quote,
    refetch,
    swapFee,
    withdrawPanelClass,
    withdrawSymbol: withdrawType === "base" ? base?.symbol : quote?.symbol,
    withdrawAmount: minPositionAmount?.gt(0)
      ? minPositionAmount
      : state.direction === "short" && shouldWithdrawBase
      ? realBaseAmount
      : realQuoteAmount,

    realAmount,
  };
};

export default useTrade;
